'use client';
import { type AuthViews } from './constants';
import {
  LinkProps,
  ToutContentProps,
  YoutubeLinkProps,
  PlaylistItem,
  ProductInfoProps,
  ProductProps,
  MediaType,
} from '@repo/ui/src/types';
import { Region } from '@repo/ui/src/types/LanguageSelector';
import { type ErrorModalProps } from './components/ErrorModal';

// types
export enum ModalID {
  cart = 'cart',
  login = 'login',
  error = 'error',
  outOfStock = 'out-of-stock',
  notifyMe = 'notify-me',
  country = 'country',
  image = 'image',
  grillRecommendation = 'grillRecommendation',
  video = 'video',
  languageSelector = 'language-selector',
  checkoutLoader = 'checkout-loader',
  shoppableIngredients = 'shoppable-ingredients',
  learnMore = 'learn-more',
}

export type OpenModalsType = Map<ModalID, any | true>;
export type ListenerCB = (isOpen: boolean) => void;
export type ToggleListenerFunc = (id: ModalID, func: ListenerCB) => void;

export interface ModalData extends Record<ModalID, any> {
  [ModalID.cart]: void;
  [ModalID.login]: { redirect?: string; view?: AuthViews } | void;
  [ModalID.outOfStock]: ProductProps;
  [ModalID.notifyMe]: ProductInfoProps;
  [ModalID.country]: void;
  [ModalID.error]: ErrorModalProps | void;
  [ModalID.shoppableIngredients]: Array<any>;
  [ModalID.learnMore]: ProductProps;
  [ModalID.image]: {
    instagramHandle?: string;
    image?: MediaType;
    imgLink?: string;
    recommendedProduct: string;
  };
  [ModalID.grillRecommendation]: Exclude<ToutContentProps['grillRecommendation'], undefined>;
  [ModalID.languageSelector]: {
    regions?: Array<Region>;
    title?: string;
  };
  [ModalID.video]: {
    link?: { link: LinkProps };
    youtubeLink: YoutubeLinkProps;
    playlistItems?: Array<PlaylistItem>;
  };
  [ModalID.checkoutLoader]: void;
}
export type PushModal = <ID extends ModalID>(
  ...args: ModalData[ID] extends void ? [id: ID] : [id: ID, data: ModalData[ID]]
) => void;
export interface UIContextType {
  openModalsMap: OpenModalsType;
  openModalIds: Array<ModalID>;
  pushModal: PushModal;
  closeModal: (id?: ModalID) => void;
  enableID: (id: ModalID) => void;
  disableID: (id: ModalID) => void;
  addListener: ToggleListenerFunc;
  removeListener: ToggleListenerFunc;
}

export interface DrawerContextValue {
  inDrawer: boolean;
  close: () => void;
  closeAll: () => void;
  index: number;
  id: ModalID | null;
}

// component
export { UIProvider } from '@repo/ui/src/modules/UI/components/UIProvider';
export { DrawerHeader } from '@repo/ui/src/modules/UI/components/Drawer/_DrawerHeader';
export { default as Modal } from '@repo/ui/src/modules/UI/components/Modal';

// hooks
export { useDrawer } from '@repo/ui/src/modules/UI/hooks/useDrawer';
export { useModal } from '@repo/ui/src/modules/UI/hooks/useModal';
export { useUIListener } from '@repo/ui/src/modules/UI/hooks/useUIListener';
